/**
 * A certificate, on the screen, over everything else.
 *
 * ---------------------------------------------------------------------------
 * WHY THE NATIVE <dialog>
 * ---------------------------------------------------------------------------
 * This is the first modal in the application, so it is worth saying why it is
 * not a div with a high z-index. `showModal()` gives, for free and correctly,
 * every part of a modal that is tedious and easy to get wrong: the content
 * outside it becomes inert, focus is trapped inside and restored to the button
 * that opened it on close, Escape closes it, and it is centred in the viewport
 * rather than in whatever container it happens to be rendered into.
 *
 * The `::backdrop` pseudo-element dims the page behind it. A click there closes
 * too, because a modal that can only be dismissed from one small button is a
 * modal people feel trapped in.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BLOB URL IS OWNED HERE
 * ---------------------------------------------------------------------------
 * The PDF arrives as a blob because every certificate route is authenticated —
 * an `<iframe src="/api/...">` would send no bearer token and render a 401.
 * So the fetch happens in script and the result becomes an object URL, which
 * has to be revoked or the document stays in memory for the life of the tab.
 * The effect's cleanup does that, on every change of `source` as well as on
 * unmount, so flicking between rows cannot leak one per click.
 */

import { useEffect, useRef, useState } from 'react';
import { Button, ErrorState, LoadingState } from '../../components/common';
import styles from './CertificateViewer.module.css';

export interface CertificateViewerProps {
  /** What the modal is showing, e.g. "C-000012" or "Draft — not yet issued". */
  title: string;
  /** The volunteer's name, or anything else worth saying under the title. */
  subtitle?: string;
  /**
   * Fetches the document. Changing this prop fetches again, so callers pass a
   * function that closes over the row they mean — see `useCallback` at the call
   * sites, without which every parent render would refetch the PDF.
   */
  source: () => Promise<Blob>;
  /** The filename offered when somebody saves it. */
  downloadName: string;
  onClose: () => void;
}

export function CertificateViewer({
  title,
  subtitle,
  source,
  downloadName,
  onClose,
}: CertificateViewerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  /*
   * Opened once, on mount: the parent renders this component only while the
   * modal should be showing, so mounting *is* opening.
   *
   * `showModal` is guarded because jsdom does not implement it. Without the
   * fallback every test that opens a preview throws on a method the browser
   * has had for years, which would be a test environment dictating the
   * component's design.
   */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    return () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setUrl(null);
    setError(null);

    source()
      .then((blob) => {
        // A response that arrived after the modal moved on is dropped rather
        // than shown: otherwise clicking two rows quickly can leave the second
        // row's heading above the first row's document.
        if (cancelled) return;
        blobRef.current = blob;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source]);

  function download() {
    const blob = blobRef.current;
    if (!blob) return;

    // A fresh URL for the save, because the one the iframe is using is revoked
    // when this modal closes and a download that outlives it would break.
    const saveUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = saveUrl;
    link.download = downloadName;
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(saveUrl), 60_000);
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="certificate-viewer-title"
      // Escape fires `cancel`, and the browser closes the dialog itself. Without
      // this the element would be hidden while the parent still thinks it is
      // open, and the next click on the same row would appear to do nothing.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // A click on the backdrop has the dialog itself as its target; a click on
      // anything inside has that child. So this closes on the backdrop only.
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className={styles.panel}>
        <header className={styles.bar}>
          <div className={styles.heading}>
            <h2 className={styles.title} id="certificate-viewer-title">
              {title}
            </h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={download} disabled={url === null}>
              Download
            </Button>
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close the certificate"
            >
              {/* Decorative: the accessible name is on the button itself. */}
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M5 5 L15 15 M15 5 L5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {error ? (
            <ErrorState error={error} message="The certificate could not be opened." />
          ) : url === null ? (
            <LoadingState label="Preparing the certificate…" />
          ) : (
            /*
             * `<iframe>` rather than `<object>` or `<embed>`: it is the one of
             * the three that takes a title, which is what a screen reader
             * announces, and the browser's own PDF viewer handles zoom and
             * printing for free.
             */
            /*
             * `#navpanes=0&view=FitH`: a certificate is one page, so the
             * viewer's thumbnail sidebar is a third of the width spent on a
             * list of one, which forces the document down to ~57% zoom. The
             * toolbar stays — its zoom and print controls are worth having.
             * Viewers that do not understand the fragment ignore it.
             */
            <iframe
              className={styles.frame}
              src={`${url}#navpanes=0&view=FitH`}
              title={`${title} — certificate`}
            />
          )}
        </div>
      </div>
    </dialog>
  );
}
